import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Home, ArrowLeft, MapPin, Calendar, DollarSign, FileText, Phone, Calculator, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function SubmitJobRequest() {
  const { categoryId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Estimate data from EstimateBuilder
  const [estimateData, setEstimateData] = useState(null);

  const [formData, setFormData] = useState({
    category_id: categoryId || "",
    title: "",
    description: "",
    address: "",
    phone: user?.phone || "",
    preferred_date: "",
    budget_range: "",
    num_contractors: "3"
  });

  useEffect(() => {
    if (categoryId) {
      setFormData(prev => ({ ...prev, category_id: categoryId }));
    }
  }, [categoryId]);

  const loadEstimateData = useCallback(() => {
    const stored = sessionStorage.getItem("estimateData");
    if (!stored) return;
    try {
      const data = JSON.parse(stored);
      setEstimateData(data);
      if (data.category_id) {
        setFormData(prev => ({ ...prev, category_id: data.category_id }));
      }
      // Auto-generate budget range based on estimate
      if (data.totals) {
        const range = `$${data.totals.totalLow.toLocaleString()} – $${data.totals.totalHigh.toLocaleString()}`;
        setFormData(prev => (prev.title ? prev : { ...prev, budget_range: range }));
      }
    } catch (e) {
      /* silent - error deliberately ignored */
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      /* silent - error deliberately ignored */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    loadEstimateData();
  }, [fetchCategories, loadEstimateData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Please log in to submit a request");
      navigate('/login', { state: { from: `/submit-request/${formData.category_id}` } });
      return;
    }

    setSubmitting(true);
    
    try {
      const requestData = {
        ...formData,
        num_contractors: parseInt(formData.num_contractors)
      };
      
      // Include estimate data if available
      if (estimateData) {
        requestData.estimate = {
          line_items: estimateData.line_items || [],
          bundle_ids: estimateData.bundle_ids || [],
          addon_ids: estimateData.addon_ids || []
        };
      }
      
      await axios.post(`${API}/job-requests`, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Clear estimate data from session
      sessionStorage.removeItem("estimateData");
      
      toast.success("Job request submitted successfully! Contractors will contact you soon.");
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCategory = categories.find(c => c.category_id === formData.category_id);

  const formatPrice = (low, high) => {
    if (low === high) return `$${low.toLocaleString()}`;
    return `$${low.toLocaleString()} – $${high.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="submit-job-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-manrope font-bold text-xl text-[#1F2933]">Contractor Hub LLC</span>
          </Link>
          
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-[#1F2933]/70"
          >
            <ArrowLeft className="w-5 h-5 mr-2" /> Back
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="font-manrope font-bold text-3xl text-[#1F2933] mb-2">
            Submit a Service Request
          </h1>
          <p className="text-[#1F2933]/60">
            {estimateData ? "Review your estimate and complete your request" : "Tell us about your project and we'll connect you with qualified contractors"}
          </p>
        </div>

        {/* Estimate Summary (if coming from EstimateBuilder) */}
        {estimateData && estimateData.totals && (
          <div className="bg-gradient-to-br from-[#0FA3B1]/10 to-[#7ED957]/5 rounded-2xl p-6 border border-[#0FA3B1]/20 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
                <Calculator className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-manrope font-semibold text-lg text-[#1F2933]">Your Estimate</h2>
                <p className="text-sm text-[#1F2933]/60">Based on your selections</p>
              </div>
            </div>
            
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <div className="bg-white/80 rounded-xl p-4">
                <p className="text-sm text-[#1F2933]/60 mb-1">Labor</p>
                <p className="font-semibold text-[#1F2933]">
                  {formatPrice(estimateData.totals.laborLow, estimateData.totals.laborHigh)}
                </p>
              </div>
              <div className="bg-white/80 rounded-xl p-4">
                <p className="text-sm text-[#1F2933]/60 mb-1">Materials</p>
                <p className="font-semibold text-[#1F2933]">
                  {formatPrice(estimateData.totals.materialsLow, estimateData.totals.materialsHigh)}
                </p>
              </div>
              <div className="bg-white/80 rounded-xl p-4">
                <p className="text-sm text-[#1F2933]/60 mb-1">Total Range</p>
                <p className="font-bold text-[#0FA3B1] text-lg">
                  {formatPrice(estimateData.totals.totalLow, estimateData.totals.totalHigh)}
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {estimateData.line_items?.length > 0 && (
                <Badge className="bg-white/80 text-[#1F2933]">
                  <Check className="w-3 h-3 mr-1" /> {estimateData.line_items.length} items selected
                </Badge>
              )}
              {estimateData.bundle_ids?.length > 0 && (
                <Badge className="bg-[#7ED957]/20 text-green-800">
                  <Check className="w-3 h-3 mr-1" /> {estimateData.bundle_ids.length} package(s)
                </Badge>
              )}
              {estimateData.addon_ids?.length > 0 && (
                <Badge className="bg-[#0FA3B1]/20 text-[#0FA3B1]">
                  <Check className="w-3 h-3 mr-1" /> {estimateData.addon_ids.length} add-on(s)
                </Badge>
              )}
            </div>
            
            <Button 
              variant="link" 
              className="mt-4 p-0 h-auto text-[#0FA3B1]"
              onClick={() => navigate(`/estimate/${formData.category_id}`)}
            >
              ← Edit your estimate
            </Button>
          </div>
        )}

        {/* No Estimate - Prompt to Build One */}
        {!estimateData && (
          <div className="bg-[#0FA3B1]/5 rounded-2xl p-6 border border-[#0FA3B1]/20 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#0FA3B1]/20 flex items-center justify-center flex-shrink-0">
                <Calculator className="w-6 h-6 text-[#0FA3B1]" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-[#1F2933] mb-1">Want to see pricing first?</h3>
                <p className="text-sm text-[#1F2933]/60 mb-3">
                  Use our estimate builder to select specific items and see a live price range before submitting your request.
                </p>
                <Button 
                  onClick={() => navigate(`/estimate/${formData.category_id || ''}`)}
                  className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white"
                  data-testid="build-estimate-btn"
                >
                  <Calculator className="w-4 h-4 mr-2" /> Build Your Estimate
                </Button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category Selection */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
            <h2 className="font-manrope font-semibold text-lg text-[#1F2933] mb-4">Service Type</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="category">Category *</Label>
                <Select 
                  value={formData.category_id} 
                  onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="category-select">
                    <SelectValue placeholder="Select a service category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.category_id} value={cat.category_id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedCategory && (
                <div className="flex items-center gap-4 p-4 bg-[#0FA3B1]/5 rounded-xl">
                  <img 
                    src={selectedCategory.image_url} 
                    alt={selectedCategory.name}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div>
                    <p className="font-medium text-[#1F2933]">{selectedCategory.name}</p>
                    <p className="text-sm text-[#1F2933]/60">{selectedCategory.description}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Project Details */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
            <h2 className="font-manrope font-semibold text-lg text-[#1F2933] mb-4">Project Details</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Project Title *</Label>
                <div className="relative mt-2">
                  <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1F2933]/40" />
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g., Interior painting for living room"
                    className="pl-12"
                    required
                    data-testid="title-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe the work you need done in detail..."
                  className="mt-2 min-h-[120px]"
                  required
                  data-testid="description-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="preferred_date">Preferred Date</Label>
                  <div className="relative mt-2">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1F2933]/40" />
                    <Input
                      id="preferred_date"
                      name="preferred_date"
                      type="date"
                      value={formData.preferred_date}
                      onChange={handleChange}
                      className="pl-12"
                      data-testid="date-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="budget_range">Budget Range</Label>
                  {estimateData ? (
                    <div className="mt-2 p-3 bg-[#0FA3B1]/5 rounded-lg border border-[#0FA3B1]/20">
                      <span className="text-[#0FA3B1] font-medium">
                        {formatPrice(estimateData.totals.totalLow, estimateData.totals.totalHigh)}
                      </span>
                      <p className="text-xs text-[#1F2933]/50 mt-1">From your estimate</p>
                    </div>
                  ) : (
                    <Select 
                      value={formData.budget_range} 
                      onValueChange={(value) => setFormData({ ...formData, budget_range: value })}
                    >
                      <SelectTrigger className="mt-2" data-testid="budget-select">
                        <DollarSign className="w-5 h-5 text-[#1F2933]/40 mr-2" />
                        <SelectValue placeholder="Select budget" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under-500">Under $500</SelectItem>
                        <SelectItem value="500-1000">$500 - $1,000</SelectItem>
                        <SelectItem value="1000-2500">$1,000 - $2,500</SelectItem>
                        <SelectItem value="2500-5000">$2,500 - $5,000</SelectItem>
                        <SelectItem value="5000-plus">$5,000+</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contact & Location */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
            <h2 className="font-manrope font-semibold text-lg text-[#1F2933] mb-4">Contact & Location</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="address">Service Address *</Label>
                <div className="relative mt-2">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1F2933]/40" />
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="123 Main St, Austin, TX 78701"
                    className="pl-12"
                    required
                    data-testid="address-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <div className="relative mt-2">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1F2933]/40" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(512) 555-0123"
                    className="pl-12"
                    required
                    data-testid="phone-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Number of Contractors */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
            <h2 className="font-manrope font-semibold text-lg text-[#1F2933] mb-2">
              How Many Contractors?
            </h2>
            <p className="text-sm text-[#1F2933]/60 mb-4">
              Choose how many contractors you'd like to contact you with quotes
            </p>
            
            <Select 
              value={formData.num_contractors} 
              onValueChange={(value) => setFormData({ ...formData, num_contractors: value })}
            >
              <SelectTrigger data-testid="num-contractors-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 Contractor</SelectItem>
                <SelectItem value="2">2 Contractors</SelectItem>
                <SelectItem value="3">3 Contractors (Recommended)</SelectItem>
              </SelectContent>
            </Select>
            
            <p className="mt-3 text-sm text-[#7ED957] font-medium">
              Pro tip: Getting multiple quotes helps you find the best price!
            </p>
          </div>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              type="submit"
              className="flex-1 bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl btn-shine"
              disabled={submitting}
              data-testid="submit-request-btn"
            >
              {submitting ? (
                <div className="spinner w-5 h-5 border-2 border-white/20 border-t-white"></div>
              ) : (
                "Submit Request"
              )}
            </Button>
            
            <Button 
              type="button"
              variant="outline"
              className="py-6 rounded-xl"
              onClick={() => navigate('/')}
            >
              Cancel
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
